import { DerbyRngV1 } from '../rng';
import {
  DRILL_TICK_S,
  MASK_PRESSED,
  type MinigameDef,
  type MinigameSession,
  type SessionScore,
} from './framework';

/**
 * Acceleration @ the Starting Gates — reaction rounds. The gate holds a
 * randomized beat, the bell rings, PRESS. Jump the bell and it's a false
 * start; three of those and the steward sends everyone home. Higher
 * tiers add distractor flashes, a settle press after the break, and a
 * tighter perfect window.
 */

export interface GatesTierConfig {
  rounds: number;
  perfectTicks: number;
  distractors: boolean;
  settlePress: boolean;
}

export function gatesTier(tier: number): GatesTierConfig {
  switch (Math.max(1, Math.min(4, tier))) {
    case 1:
      return { rounds: 3, perfectTicks: 5, distractors: false, settlePress: false };
    case 2:
      return { rounds: 3, perfectTicks: 5, distractors: true, settlePress: false };
    case 3:
      return { rounds: 4, perfectTicks: 5, distractors: true, settlePress: true };
    default:
      return { rounds: 4, perfectTicks: 4, distractors: true, settlePress: true };
  }
}

export type GatesPhase = 'hold' | 'live' | 'settle' | 'gap' | 'over';

const LIVE_TIMEOUT_TICKS = 20;
const SETTLE_MARK_TICKS = 10;
const SETTLE_WINDOW_TICKS = 4;
const GAP_TICKS = 15;
const FALSE_START_GAP_TICKS = 25;
const MAX_FALSE_STARTS = 3;

export type GatesEvent =
  | { kind: 'armed'; round: number }
  | { kind: 'bell' }
  | { kind: 'distractor' }
  | { kind: 'break'; grade: number; reactionTicks: number }
  | { kind: 'settleHit' }
  | { kind: 'falseStart'; count: number }
  | { kind: 'miss' }
  | { kind: 'done' };

export class GatesSession implements MinigameSession {
  readonly config: GatesTierConfig;

  phase: GatesPhase = 'hold';
  round = 0;
  falseStarts = 0;
  lastReactionTicks: number | null = null;
  private phaseTick = 0;
  private holdTicks: number;
  private distractorTick: number;
  private readonly rng: DerbyRngV1;
  private readonly roundScores: number[] = [];
  private currentGrade = 0;
  private streak = 0;
  private maxStreak = 0;
  private done = false;
  private readonly events: GatesEvent[] = [];

  constructor(tier: number, seed: number) {
    this.config = gatesTier(tier);
    this.rng = new DerbyRngV1(seed).fork('gates');
    this.holdTicks = this.drawHold();
    this.distractorTick = this.drawDistractor();
    this.events.push({ kind: 'armed', round: 0 });
  }

  get finished(): boolean {
    return this.done;
  }

  get phaseElapsedTicks(): number {
    return this.phaseTick;
  }

  get settleTargetTick(): number {
    return SETTLE_MARK_TICKS;
  }

  get settleWindowTicks(): number {
    return SETTLE_WINDOW_TICKS;
  }

  drainEvents(): GatesEvent[] {
    return this.events.splice(0, this.events.length);
  }

  private drawHold(): number {
    return Math.round(this.rng.range(0.6, 1.5) / DRILL_TICK_S);
  }

  private drawDistractor(): number {
    if (!this.config.distractors || !this.rng.chance(0.55)) return -1;
    // Somewhere in the middle of the hold, never at the bell itself.
    return Math.round(this.holdTicks * this.rng.range(0.35, 0.75));
  }

  private gradeFor(reactionTicks: number): number {
    if (reactionTicks <= this.config.perfectTicks) return 1;
    if (reactionTicks <= 7) return 0.8;
    if (reactionTicks <= 10) return 0.5;
    if (reactionTicks <= 15) return 0.2;
    return 0.2;
  }

  private finishRound(score: number, clean: boolean): void {
    this.roundScores.push(score);
    if (clean && score >= 0.8) {
      this.streak += 1;
      this.maxStreak = Math.max(this.maxStreak, this.streak);
    } else {
      this.streak = 0;
    }
    this.round += 1;
    if (this.round >= this.config.rounds) {
      this.done = true;
      this.events.push({ kind: 'done' });
      return;
    }
    this.phase = 'gap';
    this.phaseTick = 0;
  }

  step(mask: number): void {
    if (this.done) return;
    const pressed = (mask & MASK_PRESSED) !== 0;

    switch (this.phase) {
      case 'hold': {
        if (pressed) {
          this.falseStarts += 1;
          this.events.push({ kind: 'falseStart', count: this.falseStarts });
          if (this.falseStarts >= MAX_FALSE_STARTS) {
            this.roundScores.push(0);
            this.done = true;
            this.events.push({ kind: 'done' });
            return;
          }
          this.roundScores.push(0);
          this.streak = 0;
          this.round += 1;
          if (this.round >= this.config.rounds) {
            this.done = true;
            this.events.push({ kind: 'done' });
            return;
          }
          this.phase = 'gap';
          this.phaseTick = -(FALSE_START_GAP_TICKS - GAP_TICKS); // longer sit
          return;
        }
        if (this.phaseTick === this.distractorTick) this.events.push({ kind: 'distractor' });
        this.phaseTick += 1;
        if (this.phaseTick >= this.holdTicks) {
          this.phase = 'live';
          this.phaseTick = 0;
          this.events.push({ kind: 'bell' });
        }
        break;
      }
      case 'live': {
        if (pressed) {
          const reaction = this.phaseTick;
          this.lastReactionTicks = reaction;
          this.currentGrade = this.gradeFor(reaction);
          this.events.push({ kind: 'break', grade: this.currentGrade, reactionTicks: reaction });
          if (this.config.settlePress) {
            this.phase = 'settle';
            this.phaseTick = 0;
          } else {
            this.finishRound(this.currentGrade, true);
          }
          return;
        }
        this.phaseTick += 1;
        if (this.phaseTick >= LIVE_TIMEOUT_TICKS) {
          this.lastReactionTicks = null;
          this.events.push({ kind: 'miss' });
          this.finishRound(0, false);
        }
        break;
      }
      case 'settle': {
        if (pressed) {
          const hit = Math.abs(this.phaseTick - SETTLE_MARK_TICKS) <= SETTLE_WINDOW_TICKS;
          if (hit) this.events.push({ kind: 'settleHit' });
          this.finishRound(this.currentGrade * (hit ? 1 : 0.75), true);
          return;
        }
        this.phaseTick += 1;
        if (this.phaseTick > SETTLE_MARK_TICKS + SETTLE_WINDOW_TICKS + 6) {
          this.finishRound(this.currentGrade * 0.75, true);
        }
        break;
      }
      case 'gap': {
        this.phaseTick += 1;
        if (this.phaseTick >= GAP_TICKS) {
          this.phase = 'hold';
          this.phaseTick = 0;
          this.holdTicks = this.drawHold();
          this.distractorTick = this.drawDistractor();
          this.events.push({ kind: 'armed', round: this.round });
        }
        break;
      }
      case 'over':
        break;
    }
  }

  score(): SessionScore {
    const mean =
      this.roundScores.length > 0
        ? this.roundScores.reduce((sum, value) => sum + value, 0) / this.roundScores.length
        : 0;
    const raw = Math.max(0, Math.min(1, mean * (1 + 0.1 * this.maxStreak)));
    const stewarded = this.falseStarts >= MAX_FALSE_STARTS;
    return {
      raw,
      detail: `${this.roundScores.filter((s) => s >= 1).length} perfect breaks · streak ×${this.maxStreak}${stewarded ? ' · sent home by the steward' : ''}`,
      failed: stewarded,
    };
  }
}

export const GATES_DEF: MinigameDef = {
  id: 'gates',
  name: 'Starting Gates',
  stat: 'accel',
  energyCost: 4,
  create: (tier, seed) => new GatesSession(tier, seed),
};
